import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

function BalanceSheet({ session, companyId, companyName }) {
  const [loading, setLoading] = useState(true)
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [equity, setEquity] = useState([])
  const [editing, setEditing] = useState(null)
  const [formData, setFormData] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [selectedType, setSelectedType] = useState('asset')
  const [newAccount, setNewAccount] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    category: 'Current Assets',
    normal_balance: 'debit',
    balance: 0,
    description: ''
  })

  useEffect(() => {
    if (companyId) {
      fetchData()
    }
  }, [companyId])

  const fetchData = async () => {
    if (!companyId) return
    setLoading(true)

    try {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .order('account_code', { ascending: true })

      if (error) throw error

      // Group by type
      const assetAccounts = data?.filter(a => a.account_type === 'asset') || []
      const liabilityAccounts = data?.filter(a => a.account_type === 'liability') || []
      const equityAccounts = data?.filter(a => a.account_type === 'equity') || []

      setAssets(assetAccounts)
      setLiabilities(liabilityAccounts)
      setEquity(equityAccounts)
    } catch (err) {
      console.error('Error fetching balance sheet:', err)
      alert('Error fetching data: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (account) => {
    setEditing(account.id)
    setFormData({ balance: account.balance || 0 })
  }

  const handleSave = async (account) => {
    try {
      const { error } = await supabase
        .from('chart_of_accounts')
        .update({ 
          balance: parseFloat(formData.balance) || 0, 
          updated_at: new Date() 
        })
        .eq('id', editing)
        .eq('company_id', companyId)

      if (error) throw error
      setEditing(null)
      fetchData()
    } catch (err) {
      alert('Error updating balance: ' + err.message)
    }
  }

  const handleAddAccount = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Check if account code already exists
      const { data: existing, error: checkError } = await supabase
        .from('chart_of_accounts')
        .select('id')
        .eq('account_code', newAccount.account_code)
        .eq('company_id', companyId)
        .maybeSingle()

      if (checkError) throw checkError

      if (existing) {
        alert(`Account code ${newAccount.account_code} already exists!`)
        setLoading(false)
        return
      }

      const accountData = {
        ...newAccount,
        company_id: companyId,
        is_active: true,
        created_at: new Date(),
        updated_at: new Date()
      }

      const { error } = await supabase
        .from('chart_of_accounts')
        .insert(accountData)

      if (error) throw error

      alert('Account added successfully!')
      setShowModal(false)
      setNewAccount({
        account_code: '',
        account_name: '',
        account_type: 'asset',
        category: 'Current Assets',
        normal_balance: 'debit',
        balance: 0,
        description: ''
      })
      fetchData()
    } catch (err) {
      alert('Error adding account: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (account) => {
    if (!confirm(`Delete account "${account.account_name}"?`)) return

    try {
      const { error } = await supabase
        .from('chart_of_accounts')
        .delete()
        .eq('id', account.id)
        .eq('company_id', companyId)

      if (error) throw error
      alert('Account deleted!')
      fetchData()
    } catch (err) {
      alert('Error deleting account: ' + err.message)
    }
  }

  const downloadExcel = () => {
    const allAccounts = [...assets, ...liabilities, ...equity]
    const data = allAccounts.map(a => ({
      'Type': a.account_type,
      'Code': a.account_code,
      'Account Name': a.account_name,
      'Category': a.category,
      'Balance': a.balance || 0
    }))

    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet')
    XLSX.writeFile(wb, `${companyName}_BalanceSheet_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const downloadPDF = () => {
    const doc = new jsPDF()
    
    // Header
    doc.setFontSize(20)
    doc.text(`${companyName || 'Company'} - Balance Sheet`, 14, 20)
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30)
    
    // Calculate totals
    const totalAssets = assets.reduce((sum, a) => sum + (a.balance || 0), 0)
    const totalLiabilities = liabilities.reduce((sum, a) => sum + (a.balance || 0), 0)
    const totalEquity = equity.reduce((sum, a) => sum + (a.balance || 0), 0)

    // Prepare table data
    const tableData = [
      ['Category', 'Account', 'Balance'],
      ['--- ASSETS ---', '', totalAssets.toLocaleString()],
      ...assets.map(a => ['', a.account_name, (a.balance || 0).toLocaleString()]),
      ['--- LIABILITIES ---', '', totalLiabilities.toLocaleString()],
      ...liabilities.map(a => ['', a.account_name, (a.balance || 0).toLocaleString()]),
      ['--- EQUITY ---', '', totalEquity.toLocaleString()],
      ...equity.map(a => ['', a.account_name, (a.balance || 0).toLocaleString()]),
      ['', '--- TOTAL ---', (totalAssets).toLocaleString()]
    ]

    if (doc.autoTable) {
      doc.autoTable({
        body: tableData,
        startY: 40,
        theme: 'striped',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [30, 58, 95] }
      })
    } else {
      doc.text('Export data available in Excel format', 14, 50)
    }

    doc.save(`${companyName}_BalanceSheet_${new Date().toISOString().split('T')[0]}.pdf`)
  }

  const totalAssets = assets.reduce((sum, a) => sum + (a.balance || 0), 0)
  const totalLiabilities = liabilities.reduce((sum, a) => sum + (a.balance || 0), 0)
  const totalEquity = equity.reduce((sum, a) => sum + (a.balance || 0), 0)
  const isBalanced = totalAssets === (totalLiabilities + totalEquity)

  // Styles
  const containerStyle = {
    padding: '20px'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  }

  const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e3a5f',
    margin: 0
  }

  const addBtn = {
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  }

  const downloadBtn = {
    background: '#059669',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px'
  }

  const cardStyle = {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  }

  const cardTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e2e8f0'
  }

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f1f5f9'
  }

  const totalRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '12px',
    marginTop: '12px',
    borderTop: '2px solid #1e3a5f',
    fontWeight: 'bold',
    fontSize: '16px'
  }

  const editInputStyle = {
    width: '100px',
    padding: '4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '14px'
  }

  const saveBtnStyle = {
    marginLeft: '8px',
    padding: '4px 12px',
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  }

  const editBtnStyle = {
    marginLeft: '8px',
    padding: '4px 12px',
    background: '#2c7da0',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  }

  const deleteBtnStyle = {
    marginLeft: '8px',
    padding: '4px 12px',
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  }

  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  }

  const modalStyle = {
    background: '#fff',
    borderRadius: '20px',
    padding: '30px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto'
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  }

  const labelStyle = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '500',
    fontSize: '14px',
    color: '#1e293b'
  }

  const modalBtnStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  }

  const balanceCheckStyle = {
    marginTop: '20px',
    padding: '16px',
    borderRadius: '12px',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '600',
    background: isBalanced ? '#d1fae5' : '#fee2e2',
    color: isBalanced ? '#059669' : '#dc2626'
  }

  if (loading && assets.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading Balance Sheet...</p>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>⚖️ Balance Sheet</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowModal(true)} style={addBtn}>
            ➕ Add Account
          </button>
          <button onClick={downloadExcel} style={downloadBtn}>
            📥 Download Excel
          </button>
          <button onClick={downloadPDF} style={{ ...downloadBtn, background: '#2c7da0' }}>
            📄 Download PDF
          </button>
        </div>
      </div>

      <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '14px' }}>
        Balance Sheet as of {new Date().toLocaleDateString()} - {companyName}
      </p>

      {/* Grid */}
      <div style={gridStyle}>
        {/* Assets Column */}
        <div style={cardStyle}>
          <h3 style={{ ...cardTitleStyle, color: '#059669' }}>💵 Assets</h3>
          {assets.map(a => (
            <div key={a.id} style={rowStyle}>
              <span style={{ fontSize: '14px' }}>{a.account_name}</span>
              {editing === a.id ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    style={editInputStyle}
                  />
                  <button onClick={() => handleSave(a)} style={saveBtnStyle}>Save</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ${(a.balance || 0).toLocaleString()}
                  </span>
                  <button onClick={() => handleEdit(a)} style={editBtnStyle}>Edit</button>
                  <button onClick={() => handleDelete(a)} style={deleteBtnStyle}>Delete</button>
                </div>
              )}
            </div>
          ))}
          <div style={totalRowStyle}>
            <span>Total Assets</span>
            <span>${totalAssets.toLocaleString()}</span>
          </div>
        </div>

        {/* Liabilities + Equity Column */}
        <div>
          {/* Liabilities */}
          <div style={{ ...cardStyle, marginBottom: '16px' }}>
            <h3 style={{ ...cardTitleStyle, color: '#f59e0b' }}>📋 Liabilities</h3>
            {liabilities.map(a => (
              <div key={a.id} style={rowStyle}>
                <span style={{ fontSize: '14px' }}>{a.account_name}</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ${(a.balance || 0).toLocaleString()}
                  </span>
                  <button onClick={() => handleDelete(a)} style={deleteBtnStyle}>Delete</button>
                </div>
              </div>
            ))}
            <div style={totalRowStyle}>
              <span>Total Liabilities</span>
              <span>${totalLiabilities.toLocaleString()}</span>
            </div>
          </div>

          {/* Equity */}
          <div style={cardStyle}>
            <h3 style={{ ...cardTitleStyle, color: '#1e3a5f' }}>🏦 Equity</h3>
            {equity.map(a => (
              <div key={a.id} style={rowStyle}>
                <span style={{ fontSize: '14px' }}>{a.account_name}</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    ${(a.balance || 0).toLocaleString()}
                  </span>
                  <button onClick={() => handleDelete(a)} style={deleteBtnStyle}>Delete</button>
                </div>
              </div>
            ))}
            <div style={totalRowStyle}>
              <span>Total Equity</span>
              <span>${totalEquity.toLocaleString()}</span>
            </div>
          </div>

          {/* Balance Check */}
          <div style={balanceCheckStyle}>
            {isBalanced ? (
              <span>✅ Balanced! Assets = Liabilities + Equity</span>
            ) : (
              <span>
                ⚠️ Not Balanced! Assets (${totalAssets.toLocaleString()}) ≠ Liabilities + Equity (${(totalLiabilities + totalEquity).toLocaleString()})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Add Account Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: '0 0 20px 0' }}>
              ➕ Add New Account
            </h2>

            <form onSubmit={handleAddAccount}>
              <label style={labelStyle}>Account Type *</label>
              <select
                value={newAccount.account_type}
                onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })}
                style={inputStyle}
                required
              >
                <option value="asset">💵 Asset</option>
                <option value="liability">📋 Liability</option>
                <option value="equity">🏦 Equity</option>
              </select>

              <label style={labelStyle}>Account Code *</label>
              <input
                type="text"
                placeholder="e.g., 1050"
                value={newAccount.account_code}
                onChange={(e) => setNewAccount({ ...newAccount, account_code: e.target.value })}
                style={inputStyle}
                required
              />

              <label style={labelStyle}>Account Name *</label>
              <input
                type="text"
                placeholder="e.g., Office Equipment"
                value={newAccount.account_name}
                onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                style={inputStyle}
                required
              />

              <label style={labelStyle}>Category</label>
              <select
                value={newAccount.category}
                onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                style={inputStyle}
              >
                <option value="Current Assets">Current Assets</option>
                <option value="Fixed Assets">Fixed Assets</option>
                <option value="Inventory">Inventory</option>
                <option value="Current Liabilities">Current Liabilities</option>
                <option value="Long-term Liabilities">Long-term Liabilities</option>
                <option value="Equity">Equity</option>
              </select>

              <label style={labelStyle}>Normal Balance</label>
              <select
                value={newAccount.normal_balance}
                onChange={(e) => setNewAccount({ ...newAccount, normal_balance: e.target.value })}
                style={inputStyle}
              >
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>

              <label style={labelStyle}>Initial Balance</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newAccount.balance}
                onChange={(e) => setNewAccount({ ...newAccount, balance: parseFloat(e.target.value) || 0 })}
                style={inputStyle}
              />

              <label style={labelStyle}>Description</label>
              <textarea
                placeholder="Brief description"
                value={newAccount.description}
                onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
              />

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...modalBtnStyle, background: '#059669', color: '#fff' }}>
                  Add Account
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ ...modalBtnStyle, background: '#6b7280', color: '#fff' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BalanceSheet